import React from 'react';
import { motion } from 'framer-motion';
import { FaFingerprint, FaLayerGroup, FaLightbulb } from 'react-icons/fa';

const About = () => {
    return (
        <section id="about" className="py-6 md:py-10 bg-slate-50 relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Compact Layout with items-stretch for equal height */}
                <div className="flex flex-col lg:flex-row gap-10 items-stretch">

                    {/* Left Column - Image Composition */}
                    <div className="lg:w-1/2 relative">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="relative z-10 rounded-xl overflow-hidden shadow-lg border border-white h-full"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                alt="Our Team Collaboration"
                                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700 filter brightness-105"
                            />
                            {/* Overlay Gradient (Light) */}
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>

                            <div className="absolute bottom-4 left-4 text-white drop-shadow-md">
                                <span className="block text-3xl font-extrabold text-white">12+</span>
                                <span className="text-xs font-bold tracking-widest uppercase">Years Experience</span>
                            </div>
                        </motion.div>

                        {/* Abstract Background Element (Light & Subtle) */}
                        <div className="absolute -top-6 -right-6 w-40 h-40 bg-secondary/10 rounded-full blur-2xl -z-10 animate-pulse"></div>
                        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10 rounded-full blur-2xl -z-10 animate-pulse delay-1000"></div>
                    </div>

                    {/* Right Column - Text Content */}
                    <div className="lg:w-1/2 flex flex-col justify-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="inline-block px-3 py-0.5 mb-3 text-[10px] font-bold tracking-wider text-primary uppercase bg-primary/10 rounded-full w-fit"
                        >
                            Who We Are
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-bold text-dark mb-4 leading-tight"
                        >
                            Architects of <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Digital Reality</span>
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-sm md:text-base text-slate font-normal mb-4 leading-relaxed"
                        >
                            At Tech Inovant, we don't just write code; we engineer the future. As a premier IT consultancy, we empower enterprises to navigate the complexities of the digital age with confidence and agility.
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="text-sm md:text-base text-slate font-normal mb-6 leading-relaxed"
                        >
                            From AI-driven automated workflows to hyper-scalable cloud architectures, our solutions are designed to be the backbone of your success.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            // 3 Cards in 1 Row (Grid of 3) on ALL screens including mobile
                            className="grid grid-cols-3 gap-2 md:gap-3"
                        >
                            {[
                                { icon: FaLightbulb, title: "Innovation", desc: "Edge Tech", color: "text-amber-500", bg: "bg-amber-50" },
                                { icon: FaFingerprint, title: "Identity", desc: "Custom Brand", color: "text-purple-500", bg: "bg-purple-50" },
                                { icon: FaLayerGroup, title: "Scalability", desc: "Future Core", color: "text-blue-500", bg: "bg-blue-50" },
                            ].map((feature, idx) => (
                                <div key={idx} className="flex flex-col items-center text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                    <div className={`w-8 h-8 rounded-full ${feature.bg} ${feature.color} flex items-center justify-center text-sm mb-2`}>
                                        <feature.icon />
                                    </div>
                                    <h4 className="text-xs font-bold text-slate-800">{feature.title}</h4>
                                    <p className="text-[10px] text-slate-500">{feature.desc}</p>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
